import {Component, computed, inject, OnInit, ViewEncapsulation} from '@angular/core';
import {FormBuilder, FormControl, ReactiveFormsModule, Validators} from "@angular/forms";
import {NgClass} from "@angular/common";
import {ActivatedRoute, Router} from "@angular/router";
import {Client} from "../../services/client.interface";
import {ClientsService} from '../../services/clients.service';
import {SnackbarService} from '@/app/shared/services/snackbar.service';
import {noSoloEspacios} from './validations';
import {cleanPhoneNumber} from '@/app/utils/string-utils';
import {MatButtonModule} from '@angular/material/button';
import {MatDialogModule} from '@angular/material/dialog';
import {TitlePagesComponent} from '@/app/shared/components/title-pages-component/title-pages-component';
import {ThemeService} from '@/app/core/services/theme/theme.service';
import {AppConstants} from '@/app/constants/AppConstants';

@Component({
  selector: 'app-clientes-form-component',
  standalone:true,
  imports: [
    ReactiveFormsModule,
    NgClass,
    TitlePagesComponent,
    MatDialogModule,
    MatButtonModule,
  ],
  templateUrl: './clientes-form-component.html',
  styleUrl: './clientes-form-component.scss',
  encapsulation: ViewEncapsulation.None
})
export class ClientesFormComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly formBuilder = inject(FormBuilder);
  formUser = this.formBuilder.group({
    name: new FormControl('', [Validators.required, noSoloEspacios()]),
    businessName: new FormControl('', [Validators.required, noSoloEspacios()]),
    rfc: new FormControl('', [Validators.required, Validators.pattern(/^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/i)]),
    fiscalAddress: new FormControl(''),
    contactName: new FormControl(''),
    contactPhone: new FormControl('', [Validators.pattern(/^\d{10}$/)]),
    contactEmail: new FormControl('', [Validators.email]),
  });
  private readonly themeService = inject(ThemeService);
  private readonly clientService = inject(ClientsService);

  private client!: Client;
  protected id: string | null = "";
  protected submitted= false;
  protected isEditMode = false;
  protected themeContent = this.themeService.themeContent;
  protected clientsFormComponent = computed(() => this.themeContent()?.clientsFormComponent);

  pageData = computed(() => ({
    title: this.clientsFormComponent()?.title || AppConstants.CLIENTS_CREATE_TITLE,
    subTitle: this.clientsFormComponent()?.subtitle || AppConstants.CLIENTS_CREATE_SUBTITLE
  }));

  ngOnInit(): void {
    this.id = this.route.snapshot.paramMap.get('id');
    if(this.id !== null){
      this.pageData = computed(() => ({
        title: this.clientsFormComponent()?.editTitle || AppConstants.CLIENTS_EDIT_TITLE,
        subTitle: this.clientsFormComponent()?.editSubtitle || AppConstants.CLIENTS_EDIT_SUBTITLE
      }));
      this.isEditMode = true;
    }
    this.loadCliente(this.id);
  }

  private readonly snackbarService = inject(SnackbarService);

  onSubmit() {
    this.submitted = true;
    if (this.formUser.invalid) {
      return;
    }
    let clienteData =  {
      name: this.formUser.value.name?.trim() ?? '',
      businessName: this.formUser.value.businessName?.trim() ?? '',
      rfc: this.formUser.value.rfc ?? '',
      fiscalAddress: this.formUser.value.fiscalAddress?.trim() ?? '',
      contactName: this.formUser.value.contactName?.trim() ?? '',
      contactPhone: this.formUser.value.contactPhone ?? '',
      contactEmail: this.formUser.value.contactEmail ?? '',
    };

    if (this.id && (this.id !== "" || this.id !== null)) {
      this.clientService.updateClient(this.id, clienteData)
        .subscribe({
        next: () => {
          this.snackbarService.showSuccess('Cliente editado con éxito');
          this.formUser.reset();
          this.clientService.refreshClients(); // Refresh clients list after edit
          this.router.navigate(['/clientes']);
        },
          error: (err) => {
            this.snackbarService.showError('Error al editar el cliente '+ err.message);
          }
        });
    } else {
      // Crear nuevo cliente
      this.clientService.createClient(clienteData).subscribe({
        next: () => {
          this.snackbarService.showSuccess('Cliente creado con éxito ');
          this.formUser.reset();
          this.clientService.refreshClients(); // Refresh clients list after creation
          this.router.navigate(['/clientes']);
        },
        error: (err) => {
          this.snackbarService.showError('Error al crear el cliente: ' + err.message);
        }
      });
    }
  }

  private loadCliente(id: string | null): void {
    if (id !== null) {
      this.clientService.getClient(id).subscribe({
        next: (cliente) => {
          this.client = cliente;
          this.formUser.patchValue({
            name: cliente.name,
            businessName: cliente.businessName,
            rfc: cliente.rfc,
            fiscalAddress: cliente.fiscalAddress,
            contactName: cliente.contactName,
            contactPhone: cleanPhoneNumber(cliente.contactPhone.trim()),
            contactEmail: cliente.contactEmail
          });
        },
        error: (err) => {
          console.error('Error al obtener cliente desde el servicio:', err);
        }
      });
    }
  }

  onCancel(): void {
    this.router.navigate(['/clientes']);
  }

  get name() {return this.formUser.get('name');}
  get businessName(){return this.formUser.get('businessName');}
  get rfc(){return this.formUser.get('rfc');}
  get fiscalAddress(){return this.formUser.get('fiscalAddress');}
  get contactName(){return this.formUser.get('contactName');}
  get contactPhone(){return this.formUser.get('contactPhone');}
  get contactEmail(){return this.formUser.get('contactEmail');}

}
